<script setup lang="ts">
import UberQuote from './UberQuote.vue';
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { Dialog, DialogTrigger, DialogScrollContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import AdvancedOptionsView from './AdvancedOptionsView.vue';
import { courierRequestState_perOrderRef } from './state';
import CourierList from './CourierList.vue';
import { type storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
import { courierRequestEvents, getCloseCourierDialogEventName } from './events';
type OrderDetailsType = ResType<typeof storeOrdersApiClient[":storeId"]['get_by_order_ids']['$post']>["data"][number];

const props = defineProps<{
    order: OrderDetailsType;
}>();

function onCourierRequestStateReady(e: Event) {
    console.log('courierRequestStateReady', (e as CustomEvent).detail);
    courierListDialogOpen.value = true;
}

function onCloseCourierDialog(e: Event) {
    console.log('courierOrderFinished', (e as CustomEvent).detail);
    courierListDialogOpen.value = false;
    courierRequestDialogOpen.value = false;
}

onMounted(() => {
    window.addEventListener(`${props.order.storeOrderId}-courierRequestStateReady`, onCourierRequestStateReady);
    courierRequestEvents.addEventListener(getCloseCourierDialogEventName(props.order.storeOrderId), onCloseCourierDialog);
});

onUnmounted(() => {
    window.removeEventListener(`${props.order.storeOrderId}-courierRequestStateReady`, onCourierRequestStateReady);
    courierRequestEvents.removeEventListener(getCloseCourierDialogEventName(props.order.storeOrderId), onCloseCourierDialog);
});

const computedUberCourierLink = computed(() => {
    // { "latitude": 37.775231, "longitude": -122.417528, "addressLine1": "Uber HQ, Market Street, San Francisco, CA, USA" }
    const state = courierRequestState_perOrderRef.value[props.order.storeOrderId];
    if (
        !state.pickupAddress ||
        !state.pickupAddress.lat ||
        !state.pickupAddress.lng ||
        !state.pickupAddress.address1 ||

        !state.deliveryAddress ||
        !state.deliveryAddress.lat ||
        !state.deliveryAddress.lng ||
        !state.deliveryAddress.address1
    ) {
        return null;
    }
    const pickup = {
        addressLine1: [state.cachedStoreData?.base?.name, state.pickupAddress.address1].filter(Boolean).join(' '),
        addressLine2: [state.cachedStoreData?.base?.name, state.pickupAddress.address2].filter(Boolean).join(' '),
        latitude: state.pickupAddress.lat,
        longitude: state.pickupAddress.lng,
    };
    const delivery = {
        addressLine1: state.deliveryAddress.address1,
        addressLine2: state.deliveryAddress.address2,
        latitude: state.deliveryAddress.lat,
        longitude: state.deliveryAddress.lng,
    };
    return (
        `https://m.uber.com/go/connect/product-selection?drop[0]=${JSON.stringify(delivery)}&pickup=${JSON.stringify(pickup)}`
    );
});

/** Controlled open state for the nested "Find Courier" dialog (Radix DialogRoot: v-model:open). */
const courierListDialogOpen = ref(false);
const courierRequestDialogOpen = ref(false);
</script>

<template>
    <Dialog v-model:open="courierRequestDialogOpen">
        <DialogTrigger>
            <Button class="w-full"
                    variant="outline">
                Find Courier
            </Button>
        </DialogTrigger>
        <DialogScrollContent>
            <DialogHeader>
                <DialogTitle>
                    Find Courier
                </DialogTitle>
            </DialogHeader>
            <AdvancedOptionsView :order-details="order" />

            <Dialog v-model:open="courierListDialogOpen">
                <DialogTrigger>
                    <Button class="w-full"
                            variant="outline">
                        See offers
                    </Button>
                </DialogTrigger>
                <DialogScrollContent>
                    <!-- <DialogHeader>
                        <DialogTitle>
                            Offers
                        </DialogTitle>
                    </DialogHeader>
                     -->

                    <CourierList v-if="courierRequestState_perOrderRef[order.storeOrderId]"
                                 :storeOrderId="order.storeOrderId"
                                 :country="courierRequestState_perOrderRef[order.storeOrderId]?.pickupAddress?.country ?? ''"
                                 :store-id="order.storeId" />

                    <Dialog v-if="courierRequestState_perOrderRef[order.storeOrderId]?.isUberDirectEnabled">
                        <DialogTrigger>
                            <Button class="w-full"
                                    variant="outline">
                                Request Uber Direct Courier
                            </Button>
                        </DialogTrigger>
                        <DialogScrollContent>
                            <DialogHeader>
                                <DialogTitle>
                                    Request Uber Direct Courier
                                </DialogTitle>
                            </DialogHeader>
                            <UberQuote @close="courierRequestDialogOpen = false"
                                       :order="order" />
                        </DialogScrollContent>
                    </Dialog>

                    <div v-if="computedUberCourierLink"
                         class="mt-4 border rounded-md p-4">
                        <!-- <div class="text-xs text-muted-foreground">
                            just a shortcut that opens Uber to request a courier, with pickup and delivery addresses
                            already filled in automatically. no tracking, no integration
                        </div> -->
                        <Button class="w-full text-background-foreground underline hover:bg-secondary"
                                variant="link"
                                as="a"
                                :href="computedUberCourierLink"
                                target="_blank">
                            Uber courier deeplink
                        </Button>
                    </div>

                </DialogScrollContent>
            </Dialog>

        </DialogScrollContent>
    </Dialog>
</template>